import React, { useState, useRef, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  ListSubheader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip,
  Slider,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import MapIcon from '@mui/icons-material/Map';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DateSelector from './shared/DateSelector';
import MediaItem from './shared/MediaItem';
import { isValidImageUrl, isValidVideoUrl, getVideoThumbnail, stripHtml } from './shared/utils';
import storyEventService from '../../services/storyEventService';
import timelineService from '../../services/timelineService';
import ColorPicker from '../ColorPicker';
import RichTextEditor from './shared/RichTextEditor';

const styles = {
  fileInput: {
    display: 'none'
  }
};

const StoriesPanel = ({
  settings,
  categories,
  stylings,
  storyEvents,
  refreshStories,
  initialEditingStory,
  onDeleteStory
}) => {
  // Default story form state
  const defaultStoryForm = {
    title: '',
    description: '',
    startDate: {
      year: '',
      month: '',
      day: '',
      isBC: false
    },
    endDate: {
      year: '',
      month: '',
      day: '',
      isBC: false
    },
    order_level: 1,
    category_id: null,
    styling_id: null,
    images: [],
    imageUrl: '',
    videos: [],
    videoUrl: '',
    coverContent: '',
    coverImageUrl: '',
    showDescription: true,
    width: 150,
    height: 80,
    borderRadius: 6,
    lines: [],
    linked_category_id: null
  };

  const [showStoryForm, setShowStoryForm] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [storyForm, setStoryForm] = useState(defaultStoryForm);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt'); // 'createdAt' or 'startDate'

  // Sort stories logic
  const sortedStories = React.useMemo(() => {
    if (sortBy === 'createdAt') {
      return storyEvents;
    }

    return [...storyEvents].sort((a, b) => {
      // Helper to safely get date values
      const getDateValues = (story) => {
        const dateObj = story.startDate;

        if (!dateObj) {
          return { year: 999999, month: 0, day: 0 };
        }

        // CustomDate object - has getFullYear method
        if (typeof dateObj.getFullYear === 'function') {
          const year = dateObj.getFullYear(); // Already negative for BC
          const month = dateObj.getMonth();
          const day = dateObj.getDate();

          return { year, month, day };
        }

        // Plain object fallback (shouldn't happen after service processing)
        if (dateObj.year !== undefined) {
          let year = Number(dateObj.year);
          if (dateObj.isBC && year > 0) year = -year;

          return {
            year: year,
            month: Number(dateObj.month || 0),
            day: Number(dateObj.day || 1)
          };
        }

        return { year: 999999, month: 0, day: 0 };
      };

      const dateA = getDateValues(a);
      const dateB = getDateValues(b);

      // Compare years (negative for BC, so simple subtraction works)
      if (dateA.year !== dateB.year) {
        return dateA.year - dateB.year;
      }

      // Compare months
      if (dateA.month !== dateB.month) {
        return dateA.month - dateB.month;
      }

      // Compare days
      return dateA.day - dateB.day;
    });
  }, [storyEvents, sortBy]);

  // File input refs
  const imageInputRef = useRef(null);
  const coverImageInputRef = useRef(null); // New ref for cover image

  // Load initial editing story if provided
  useEffect(() => {
    if (initialEditingStory) {
      handleEditStoryClick(initialEditingStory);
    }
  }, [initialEditingStory]);

  // Generic form change handler
  const handleStoryChange = (field) => (event) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setStoryForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setStoryForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Helper for RichTextEditor changes (value is directly passed)
  const handleRichTextChange = (field) => (value) => {
    setStoryForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle date changes
  const handleDateChange = (dateType) => (e) => {
    const { name, value } = e.target;
    const isCheckbox = e.target.type === 'checkbox';

    setStoryForm(prev => {
      // If it's the BC checkbox
      if (name === 'isBC') {
        return {
          ...prev,
          [dateType]: {
            ...prev[dateType],
            isBC: e.target.checked
          }
        };
      }

      // If it's a year field, ensure it's positive
      if (name === 'year') {
        // If the input is empty or non-numeric, allow it (for usability)
        if (value === '' || isNaN(parseInt(value, 10))) {
          return {
            ...prev,
            [dateType]: {
              ...prev[dateType],
              [name]: value
            }
          };
        }

        // Ensure year is always positive regardless of BC status
        const yearValue = Math.abs(parseInt(value, 10));

        return {
          ...prev,
          [dateType]: {
            ...prev[dateType],
            [name]: yearValue
          }
        };
      }

      // For other fields, use the default behavior
      const newValue = isCheckbox ? e.target.checked : parseInt(value, 10);
      return {
        ...prev,
        [dateType]: {
          ...prev[dateType],
          [name]: newValue
        }
      };
    });
  };

  // Story operations
  const handleAddStory = () => {
    setEditingStory(null);
    setStoryForm(defaultStoryForm);
    setShowStoryForm(true);
  };

  const handleEditStoryClick = (story) => {
    setEditingStory(story._id);

    // Process startDate - ensure years are always positive
    let startYear = story.startDate ? story.startDate.getFullYear() : '';
    let startIsBC = story.startDate ? story.startDate.isBC : false;

    // If year is negative, make it positive and ensure isBC is true
    if (startYear < 0) {
      startYear = Math.abs(startYear);
      startIsBC = true;
    }

    // Process endDate - ensure years are always positive if endDate exists
    let endYear = '';
    let endIsBC = false;

    if (story.endDate) {
      endYear = story.endDate.getFullYear();
      endIsBC = story.endDate.isBC || false;

      // If year is negative, make it positive and ensure isBC is true
      if (endYear < 0) {
        endYear = Math.abs(endYear);
        endIsBC = true;
      }
    }

    setStoryForm({
      title: story.title,
      description: story.description || '',
      startDate: story.startDate ? {
        year: startYear,
        month: story.startDate.getMonth(),
        day: story.startDate.getDate(),
        isBC: startIsBC
      } : defaultStoryForm.startDate,
      endDate: story.endDate ? {
        year: endYear,
        month: story.endDate.getMonth(),
        day: story.endDate.getDate(),
        isBC: endIsBC
      } : defaultStoryForm.endDate,
      order_level: story.order_level !== undefined ? story.order_level : 1,
      category_id: story.category_id?._id || story.category_id || null,
      styling_id: story.styling_id?._id || story.styling_id || null,
      images: story.images || [],
      imageUrl: '',
      videos: story.videos || [],
      videoUrl: '',
      coverContent: story.coverContent || '',
      coverImageUrl: '',
      showDescription: story.showDescription !== undefined ? story.showDescription : true,
      width: story.width || 300,
      height: story.height || 200,
      borderRadius: story.borderRadius || 8,
      lines: Array.isArray(story.lines) ? story.lines : [],
      linked_category_id: story.linked_category_id?._id || story.linked_category_id || null
    });
    setShowStoryForm(true);
  };

  const handleCancelStory = () => {
    setShowStoryForm(false);
    setEditingStory(null);
  };

  const handleSaveStory = async () => {
    // Validation checks
    if (!storyForm.title) {
      alert('Lütfen bir başlık giriniz');
      return;
    }

    if (!storyForm.startDate.year) {
      alert('Lütfen geçerli bir yıl giriniz');
      return;
    }

    // Check if the event's start date is before the timeline's start date
    if (settings.startDate && storyForm.startDate) {
      const timelineStartDate = settings.startDate;
      const eventStartDate = storyForm.startDate;
      console.log(timelineStartDate)
      console.log(eventStartDate.year < timelineStartDate.year)

      // Fix BC date comparison logic
      let isEventStartBeforeTimelineStart;

      // Case 1: Event is BC, timeline is not BC - event is definitely before timeline
      if (eventStartDate.isBC && !timelineStartDate.isBC) {
        isEventStartBeforeTimelineStart = true;
      }
      // Case 2: Event is not BC, timeline is BC - event is definitely after timeline
      else if (!eventStartDate.isBC && timelineStartDate.isBC) {
        isEventStartBeforeTimelineStart = false;
      }
      // Case 3: Both are BC or both are not BC
      else {
        if (eventStartDate.isBC) {
          // For BC dates, HIGHER year means EARLIER (2000 BC is before 1000 BC)
          isEventStartBeforeTimelineStart = (
            eventStartDate.year > timelineStartDate.year ||
            (eventStartDate.year === timelineStartDate.year && eventStartDate.month < timelineStartDate.month) ||
            (eventStartDate.year === timelineStartDate.year && eventStartDate.month === timelineStartDate.month && eventStartDate.day < timelineStartDate.day)
          );
        } else {
          // For non-BC dates, LOWER year means EARLIER (1000 AD is before 2000 AD)
          isEventStartBeforeTimelineStart = (
            eventStartDate.year < timelineStartDate.year ||
            (eventStartDate.year === timelineStartDate.year && eventStartDate.month < timelineStartDate.month) ||
            (eventStartDate.year === timelineStartDate.year && eventStartDate.month === timelineStartDate.month && eventStartDate.day < timelineStartDate.day)
          );
        }
      }

      // Validation disabled per request - now allowing events to start before timeline start date
      /*if (isEventStartBeforeTimelineStart) {
        alert('Hata: Etkinliğin başlangıç tarihi, zaman çizelgesinin başlangıç tarihinden önce olamaz. Lütfen geçerli bir tarih girin.');
        return;
      }*/
    }

    // Check if the event's end date is after the timeline's end date
    if (settings.endDate && storyForm.endDate && storyForm.endDate.year) {
      const timelineEndDate = settings.endDate;
      const eventEndDate = storyForm.endDate;

      // Fix BC date comparison logic
      let isEventEndAfterTimelineEnd;

      // Case 1: Event is not BC, timeline is BC - event is definitely after timeline
      if (!eventEndDate.isBC && timelineEndDate.isBC) {
        isEventEndAfterTimelineEnd = true;
      }
      // Case 2: Event is BC, timeline is not BC - event is definitely before timeline
      else if (eventEndDate.isBC && !timelineEndDate.isBC) {
        isEventEndAfterTimelineEnd = false;
      }
      // Case 3: Both are BC or both are not BC
      else {
        if (eventEndDate.isBC) {
          // For BC dates, LOWER year means LATER (1000 BC is after 2000 BC)
          isEventEndAfterTimelineEnd = (
            eventEndDate.year < timelineEndDate.year ||
            (eventEndDate.year === timelineEndDate.year && eventEndDate.month > timelineEndDate.month) ||
            (eventEndDate.year === timelineEndDate.year && eventEndDate.month === timelineEndDate.month && eventEndDate.day > timelineEndDate.day)
          );
        } else {
          // For non-BC dates, HIGHER year means LATER (2000 AD is after 1000 AD)
          isEventEndAfterTimelineEnd = (
            eventEndDate.year > timelineEndDate.year ||
            (eventEndDate.year === timelineEndDate.year && eventEndDate.month > timelineEndDate.month) ||
            (eventEndDate.year === timelineEndDate.year && eventEndDate.month === timelineEndDate.month && eventEndDate.day > timelineEndDate.day)
          );
        }
      }

      // Validation disabled per request - now allowing events to end after timeline end date
      /*if (isEventEndAfterTimelineEnd) {
        alert('Hata: Etkinliğin bitiş tarihi, zaman çizelgesinin bitiş tarihinden sonra olamaz. Lütfen geçerli bir tarih girin.');
        return;
      }*/
    }

    // Process the form data before submission
    const formattedStoryForm = {
      ...storyForm,
      // Process endDate - set to null if any fields are empty
      endDate: storyForm.endDate && storyForm.endDate.year && storyForm.endDate.month && storyForm.endDate.day
        ? storyForm.endDate
        : null,
      // Ensure lines have the required 'type' field and no date fields
      lines: storyForm.lines.map(line => ({
        color: line.color || '#000000',
        opacity: line.opacity || 1,
        type: line.type || (line.thick && line.thick.length > 0 ? 'thick' : 'thin')
      }))
    };

    // Remove temporary URL fields to avoid sending them to the backend
    const { imageUrl, videoUrl, ...cleanedFormData } = formattedStoryForm;

    try {
      if (editingStory) {
        await storyEventService.updateStoryEvent(settings._id, editingStory, cleanedFormData);
      } else {
        await storyEventService.createStoryEvent(settings._id, {
          ...cleanedFormData,
          timeline_id: settings._id
        });
      }

      setShowStoryForm(false);
      await refreshStories();

      // Dispatch custom event to notify timeline of story changes
      document.dispatchEvent(new CustomEvent('timelineDataUpdated'));
    } catch (error) {
      console.error('Error saving story event:', error);
      alert('Hikaye kaydedilirken bir hata oluştu');
    }
  };

  const handleDeleteStoryClick = async (storyId) => {
    if (!window.confirm('Bu hikayeyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await storyEventService.deleteStoryEvent(settings._id, storyId);

      if (onDeleteStory) {
        onDeleteStory(storyId);
      }

      await refreshStories();

      // Dispatch custom event to notify timeline of story changes
      document.dispatchEvent(new CustomEvent('timelineDataUpdated'));
    } catch (error) {
      console.error('Error deleting story:', error);
      alert('Hikaye silinirken bir hata oluştu');
    }
  };

  // Media handling
  const handleFileUpload = async (files, mediaType) => {
    if (!files.length) return;

    setUploadingMedia(true);

    try {
      const mediaUrls = await Promise.all(
        Array.from(files).map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(file);
          });
        })
      );

      setStoryForm(prev => ({
        ...prev,
        [mediaType]: [...prev[mediaType], ...mediaUrls]
      }));
    } catch (error) {
      console.error(`Error uploading ${mediaType}:`, error);
      alert(`${mediaType} yüklenirken bir hata oluştu`);
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleRemoveMedia = (mediaType, index) => {
    setStoryForm(prev => ({
      ...prev,
      [mediaType]: prev[mediaType].filter((_, i) => i !== index)
    }));
  };

  const handleAddVideoUrl = () => {
    if (storyForm.videoUrl) {
      if (isValidVideoUrl(storyForm.videoUrl)) {
        setStoryForm(prev => ({
          ...prev,
          videos: [...prev.videos, prev.videoUrl],
          videoUrl: ''
        }));
      } else {
        alert('Lütfen geçerli bir video URL\'si giriniz (YouTube, Vimeo, Dailymotion)');
      }
    }
  };

  // Update handleLineChange to work with a single array
  const handleLineChange = (lineIndex, field) => (event) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;

    setStoryForm(prev => {
      const updatedLines = [...prev.lines];

      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updatedLines[lineIndex] = {
          ...updatedLines[lineIndex],
          [parent]: {
            ...updatedLines[lineIndex][parent],
            [child]: value
          }
        };
      } else {
        updatedLines[lineIndex] = {
          ...updatedLines[lineIndex],
          [field]: value
        };
      }

      return {
        ...prev,
        lines: updatedLines
      };
    });
  };

  // Update handleLineDateChange to work with a single array
  const handleLineDateChange = (lineIndex, dateType) => (e) => {
    const { name, value } = e.target;
    const isCheckbox = e.target.type === 'checkbox';
    const newValue = isCheckbox ? e.target.checked : parseInt(value, 10);

    setStoryForm(prev => {
      const updatedLines = [...prev.lines];
      updatedLines[lineIndex] = {
        ...updatedLines[lineIndex],
        [dateType]: {
          ...updatedLines[lineIndex][dateType],
          [name]: newValue
        }
      };

      return {
        ...prev,
        lines: updatedLines
      };
    });
  };

  // Update addLine function to remove date fields
  const addLine = () => {
    setStoryForm(prev => {
      const newLine = {
        color: '#0000FF',
        opacity: 1,
        type: 'thin' // Default to thin line
      };

      return {
        ...prev,
        lines: [...prev.lines, newLine]
      };
    });
  };

  // Update removeLine function to remove from a single array
  const removeLine = (lineIndex) => {
    setStoryForm(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== lineIndex)
    }));
  };

  // Add this function to apply a styling template
  const handleApplyStyling = (stylingId) => {
    if (!stylingId) {
      setStoryForm(prev => ({
        ...prev,
        styling_id: null,
        // Keep default values if no styling is selected
      }));
      return;
    }

    const selectedStyling = stylings.find(style => style._id === stylingId);
    if (!selectedStyling) {
      return;
    }

    setStoryForm(prev => ({
      ...prev,
      styling_id: stylingId,
      // Apply the styling values (these will be used if no styling_id is found)
      width: selectedStyling.width,
      height: selectedStyling.height,
      borderRadius: selectedStyling.borderRadius
    }));
  };

  // Handle cover image upload specifically
  const handleCoverImageUpload = async (files) => {
    if (!files.length) return;

    setUploadingMedia(true);

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setStoryForm(prev => ({
          ...prev,
          coverContent: reader.result,
          coverImageUrl: '' // Clear coverImageUrl when uploading a file
        }));
        setUploadingMedia(false);
      };
      reader.readAsDataURL(files[0]);
    } catch (error) {
      console.error('Error uploading cover image:', error);
      alert('Kapak resmi yüklenirken bir hata oluştu');
      setUploadingMedia(false);
    }
  };

  return (
    <>
      {/* Button to add new story or form heading */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
            {showStoryForm ?
              (editingStory ? 'Hikaye Düzenle' : 'Yeni Hikaye Ekle') :
              'Hikayeler'}
          </Typography>


        </Box>

        {!showStoryForm && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sıralama</InputLabel>
              <Select
                value={sortBy}
                label="Sıralama"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="createdAt">Oluşturulma Tarihi</MenuItem>
                <MenuItem value="startDate">Başlangıç Tarihi</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddStory}
              startIcon={<EditIcon />}
            >
              Hikaye Ekle
            </Button>
          </Box>
        )}
      </Box>

      {/* Story form - shown only when adding or editing */}
      {showStoryForm ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>

            <RichTextEditor
              value={storyForm.title}
              onChange={handleRichTextChange('title')}
              placeholder="Başlık..."
              minHeight="40px"
            />
          </Grid>


          <Grid item xs={12}>

            <RichTextEditor
              value={storyForm.description}
              onChange={handleRichTextChange('description')}
              placeholder="Hikaye açıklaması..."
              minHeight="200px"
            />
          </Grid>

          <Grid item xs={12} sm={6}>

            <DateSelector
              dateValue={storyForm.startDate}
              onDateChange={handleDateChange('startDate')}
              label="Başlangıç Tarihi"
              required={true}
            />

            <DateSelector
              dateValue={storyForm.endDate}
              onDateChange={handleDateChange('endDate')}
              label="Bitiş Tarihi"
              optional={true}
            />

            <TextField
              label="Sıralama Seviyesi"
              type="number"
              fullWidth
              margin="normal"
              value={storyForm.order_level}
              onChange={(e) => {
                const value = e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10));
                handleStoryChange('order_level')({ target: { value } });
              }}
              inputProps={{ min: 1 }}
              helperText="Olayların dikey sıralamasını belirler"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Kategori</InputLabel>
              <Select
                value={storyForm.category_id || ''}
                onChange={handleStoryChange('category_id')}
                label="Kategori"
              >
                <MenuItem value="">
                  <em>Kategori Seçilmedi</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={storyForm.showDescription}
                    onChange={handleStoryChange('showDescription')}
                  />
                }
                label="Açıklamayı Göster"
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* Remove the redundant Kapak İçeriği dropdown */}

            {/* Add dedicated cover image upload button with matching UI style */}
            <Box sx={{ mt: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Kapak Resmi
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ImageIcon />}
                onClick={() => coverImageInputRef.current.click()}
                disabled={uploadingMedia}
                fullWidth
              >
                Kapak Resmi Yükle
              </Button>
              <input
                type="file"
                hidden
                ref={coverImageInputRef}
                accept="image/*"
                onChange={(e) => handleCoverImageUpload(e.target.files)}
              />

              {/* Add cover image URL directly */}
              <TextField
                label="Kapak Resmi URL"
                fullWidth
                margin="normal"
                value={storyForm.coverImageUrl || ''}
                onChange={handleStoryChange('coverImageUrl')}
                InputProps={{
                  endAdornment: (
                    <Button
                      onClick={() => {
                        if (storyForm.coverImageUrl) {
                          if (isValidImageUrl(storyForm.coverImageUrl)) {
                            setStoryForm(prev => ({
                              ...prev,
                              coverContent: prev.coverImageUrl,
                              coverImageUrl: ''
                            }));
                          } else {
                            alert('Lütfen geçerli bir resim URL\'si giriniz (.jpg, .png, .gif, vb.)');
                          }
                        }
                      }}
                      disabled={!storyForm.coverImageUrl}
                    >
                      Ekle
                    </Button>
                  )
                }}
              />

              {/* Display current cover image if it exists - remove the label */}
              {storyForm.coverContent && !storyForm.images.includes(storyForm.coverContent) &&
                !storyForm.videos.includes(storyForm.coverContent) && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ position: 'relative', width: 150, height: 150 }}>
                      <Box
                        component="img"
                        src={storyForm.coverContent.startsWith('/uploads') ?
                          `${process.env.REACT_APP_API_URL}${storyForm.coverContent}` :
                          storyForm.coverContent
                        }
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }}
                        onError={(e) => {
                          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='10' text-anchor='middle' dominant-baseline='middle' fill='%23999'%3EGörsel Yok%3C/text%3E%3C/svg%3E";
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }}
                        onClick={() => setStoryForm(prev => ({ ...prev, coverContent: '' }))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )}
            </Box>

            {/* Media upload section */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Medya Ekle
              </Typography>

              {/* Images */}
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<ImageIcon />}
                  onClick={() => imageInputRef.current.click()}
                  disabled={uploadingMedia}
                  fullWidth
                >
                  Resim Ekle
                </Button>
                <input
                  type="file"
                  hidden
                  ref={imageInputRef}
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFileUpload(e.target.files, 'images')}
                />

                {/* Add image URL directly */}
                <TextField
                  label="Resim URL"
                  fullWidth
                  margin="normal"
                  value={storyForm.imageUrl || ''}
                  onChange={handleStoryChange('imageUrl')}
                  InputProps={{
                    endAdornment: (
                      <Button
                        onClick={() => {
                          if (storyForm.imageUrl) {
                            if (isValidImageUrl(storyForm.imageUrl)) {
                              setStoryForm(prev => ({
                                ...prev,
                                images: [...prev.images, prev.imageUrl],
                                imageUrl: ''
                              }));
                            } else {
                              alert('Lütfen geçerli bir resim URL\'si giriniz (.jpg, .png, .gif, vb.)');
                            }
                          }
                        }}
                        disabled={!storyForm.imageUrl}
                      >
                        Ekle
                      </Button>
                    )
                  }}
                />

                {/* Display uploaded images */}
                {storyForm.images.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {storyForm.images.map((image, index) => (
                      <MediaItem
                        key={`image-${index}`}
                        item={image}
                        index={index}
                        type="images"
                        onRemove={handleRemoveMedia}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Videos */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Video URL (YouTube, Dailymotion vs.)"
                  fullWidth
                  value={storyForm.videoUrl || ''}
                  onChange={handleStoryChange('videoUrl')}
                  InputProps={{
                    endAdornment: (
                      <Button
                        onClick={handleAddVideoUrl}
                        disabled={!storyForm.videoUrl}
                      >
                        Ekle
                      </Button>
                    )
                  }}
                />

                {/* Display added video URLs */}
                {storyForm.videos.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {storyForm.videos.map((video, index) => (
                      <Box
                        key={`video-${index}`}
                        sx={{
                          position: 'relative',
                          width: 120,
                          marginBottom: 1
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            height: 70,
                            mb: 0.5
                          }}
                        >
                          <Box
                            component="img"
                            src={getVideoThumbnail(video)}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 1
                            }}
                            onError={(e) => {
                              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='70' viewBox='0 0 120 70'%3E%3Crect width='120' height='70' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='10' text-anchor='middle' dominant-baseline='middle' fill='%23999'%3EVideo Yok%3C/text%3E%3C/svg%3E";
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: 30,
                              height: 30,
                              borderRadius: '50%',
                              bgcolor: 'rgba(255,0,0,0.7)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Box
                              sx={{
                                width: 0,
                                height: 0,
                                borderTop: '6px solid transparent',
                                borderBottom: '6px solid transparent',
                                borderLeft: '10px solid white',
                                marginLeft: '2px'
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Typography
                            variant="caption"
                            sx={{
                              maxWidth: '80%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Video {index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveMedia('videos', index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Maps */}
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<MapIcon />}
                  onClick={() => {
                    if (editingStory && settings?._id) {
                      // Open the map page for this event
                      window.open(`/cizelge/${timelineService.getTimelineUrlIdentifier(settings)}/harita?eventId=${editingStory}`, '_blank');
                    } else {
                      alert('Lütfen önce hikayeyi kaydedin, sonra haritada işaretleyici ekleyebilirsiniz.');
                    }
                  }}
                  fullWidth
                >
                  Haritada Ekle
                </Button>
              </Box>
            </Box>

            {/* Add styling selection here, after the map image URL section */}
            <FormControl fullWidth margin="normal" sx={{ mt: 3 }}>
              <InputLabel>Stil</InputLabel>
              <Select
                value={storyForm.styling_id || ''}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  // Convert empty string to null for the styling_id
                  handleStoryChange('styling_id')({
                    target: { value: selectedValue === '' ? null : selectedValue }
                  });
                  handleApplyStyling(selectedValue);
                }}
                label="Stil"
              >
                <MenuItem value="">
                  <em>Stilsiz</em>
                </MenuItem>
                {stylings && stylings.map((style) => (
                  <MenuItem key={style._id} value={style._id}>
                    {style.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="lines-panel-content"
                id="lines-panel-header"
              >
                <Typography variant="subtitle1">Çizgi Ayarları</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Lines management header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">Zaman Çizgileri</Typography>
                  <Box>
                    <Tooltip
                      title={!(storyForm.endDate && storyForm.endDate.year && storyForm.endDate.month && storyForm.endDate.day) ? "Lütfen önce bir bitiş tarihi girin" : ""}
                      arrow
                    >
                      {/* Wrap the button in a span for Tooltip when disabled */}
                      <span>
                        <Button
                          startIcon={<AddIcon />}
                          onClick={addLine}
                          size="small"
                          variant="outlined"
                          // Disable button if endDate year, month, or day is missing
                          disabled={!(storyForm.endDate && storyForm.endDate.year && storyForm.endDate.month && storyForm.endDate.day)}
                        >
                          Çizgi Ekle
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Display lines */}
                {storyForm.lines && storyForm.lines.length > 0 ? (
                  storyForm.lines.map((line, index) => (
                    <Box
                      key={`line-${index}`}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        position: 'relative',
                        borderLeft: `${line.type === 'thick' ? 20 : 4}px solid ${line.color || '#000000'}`
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            {line.type === 'thick' ? 'Kalın' : 'İnce'} Çizgi {index + 1}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Bu çizgi, olayın başlangıç ve bitiş tarihleri arasında gösterilecektir.
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                          <FormControl fullWidth size="small" sx={{ mr: 1 }}>
                            <InputLabel>Çizgi Tipi</InputLabel>
                            <Select
                              value={line.type}
                              onChange={handleLineChange(index, 'type')}
                              label="Çizgi Tipi"
                            >
                              <MenuItem value="thin">İnce Çizgi</MenuItem>
                              <MenuItem value="thick">Kalın Çizgi</MenuItem>
                            </Select>
                          </FormControl>
                          <IconButton
                            size="small"
                            onClick={() => removeLine(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                              Renk
                            </Typography>
                            <ColorPicker
                              color={line.color || '#000000'}
                              onChange={(color) => handleLineChange(index, 'color')({ target: { value: color } })}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography gutterBottom>
                            Opaklık: {line.opacity}
                          </Typography>
                          <Slider
                            value={line.opacity || 1}
                            onChange={(e, newValue) => {
                              setStoryForm(prev => {
                                const updatedLines = [...prev.lines];
                                updatedLines[index].opacity = newValue;
                                return {
                                  ...prev,
                                  lines: updatedLines
                                };
                              });
                            }}
                            min={0}
                            max={1}
                            step={0.1}
                            valueLabelDisplay="auto"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Henüz çizgi eklenmemiş
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Bağlantılı Kategori</InputLabel>
              <Select
                value={storyForm.linked_category_id || ''}
                onChange={handleStoryChange('linked_category_id')}
                label="Bağlantılı Kategori"
              >
                <MenuItem value="">
                  <em>Bağlantılı Kategori Seçilmedi</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      width: '100%'
                    }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: category.color || '#808080',
                          flexShrink: 0
                        }}
                      />
                      <Typography>{category.name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancelStory}
                sx={{ mr: 1 }}
              >
                İptal
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveStory}
                disabled={uploadingMedia}
              >
                {editingStory ? 'Hikayeyi Güncelle' : 'Hikaye Ekle'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      ) : (
        /* List of existing story events - shown when not adding/editing */
        <Box>
          {sortedStories.length === 0 ? (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              Henüz hikaye eklenmemiş
            </Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              {sortedStories.map((story) => (
                <Box
                  key={story._id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    boxShadow: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {story.coverContent && (
                      <Box
                        component="img"
                        src={story.coverContent.includes('youtube.com') || story.coverContent.includes('youtu.be') ?
                          getVideoThumbnail(story.coverContent) :
                          story.coverContent.startsWith('/uploads') ?
                            `${process.env.REACT_APP_API_URL}${story.coverContent}` :
                            story.coverContent
                        }
                        sx={{
                          width: 50,
                          height: 50,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mr: 2
                        }}
                        onError={(e) => {
                          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50' viewBox='0 0 50 50'%3E%3Crect width='50' height='50' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='8' text-anchor='middle' dominant-baseline='middle' fill='%23999'%3EGörsel Yok%3C/text%3E%3C/svg%3E";
                        }}
                      />
                    )}
                    <Box>
                      <Typography variant="subtitle1">{stripHtml(story.title)}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {story.startDate?.getDate ?
                          `${story.startDate.getDate()}/${story.startDate.getMonth() + 1}/${Math.abs(story.startDate.getFullYear())}${story.startDate.isBC ? ' MÖ' : ''}` :
                          'Tarih yok'}
                        {story.endDate?.getDate &&
                          ` - ${story.endDate.getDate()}/${story.endDate.getMonth() + 1}/${Math.abs(story.endDate.getFullYear())}${story.endDate.isBC ? ' MÖ' : ''}`
                        }
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEditStoryClick(story)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteStoryClick(story._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default StoriesPanel;